import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Trash2, Save, Loader2 } from "lucide-react";

const CATEGORIES = ["Comida", "Transporte", "Proyecto del Hogar", "Cuidado de Niños", "Petición de Oración", "Otro"];

export default function EditNeedPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [need, setNeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/needs/edit/${token}`);
      setNeed(data);
    } catch {
      setNeed(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/needs/edit/${token}`, {
        name: need.name, phone: need.phone, email: need.email,
        category: need.category, title: need.title,
        description: need.description, needed_date: need.needed_date,
      });
      toast.success("Cambios guardados");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const markComplete = async () => {
    try {
      await api.post(`/needs/edit/${token}/complete`);
      toast.success("Marcada como completada. Gracias a Dios.");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/needs/edit/${token}`);
      toast.success("Publicación eliminada");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#8A3A33]" />
      </div>
    );
  }

  if (!need) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center px-4">
        <div className="bg-white border-2 border-[#D1C9C1] rounded-2xl p-8 max-w-md text-center">
          <h1 className="font-serif text-2xl font-bold text-[#1A1818] mb-3">Enlace inválido</h1>
          <p className="text-base text-[#4A4646] mb-5">
            Este enlace de edición no es válido o la publicación ya fue eliminada.
          </p>
          <Link to="/">
            <Button className="bg-[#8A3A33] hover:bg-[#6B2B25] text-white h-12 px-6">
              Volver al inicio
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-[#8A3A33] hover:underline mb-6 text-base font-medium">
          <ArrowLeft className="w-5 h-5" /> Volver al inicio
        </Link>

        <div className="bg-white border-2 border-[#D1C9C1] rounded-2xl p-6 sm:p-8">
          <h1 className="font-serif text-3xl font-bold text-[#1A1818] mb-2">
            Administrar Necesidad
          </h1>
          <p className="text-base text-[#4A4646] mb-6">
            Estado actual: <span className="font-semibold text-[#8A3A33]">{need.status}</span>
          </p>

          <div className="space-y-4">
            <div>
              <Label className="text-base font-semibold mb-1.5 block">Nombre</Label>
              <Input data-testid="edit-name" value={need.name || ""} onChange={(e) => setNeed({ ...need, name: e.target.value })} className="h-12 text-base border-2 border-[#D1C9C1]" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-base font-semibold mb-1.5 block">Teléfono</Label>
                <Input data-testid="edit-phone" value={need.phone || ""} onChange={(e) => setNeed({ ...need, phone: e.target.value })} className="h-12 text-base border-2 border-[#D1C9C1]" />
              </div>
              <div>
                <Label className="text-base font-semibold mb-1.5 block">Correo electrónico</Label>
                <Input data-testid="edit-email" value={need.email || ""} onChange={(e) => setNeed({ ...need, email: e.target.value })} className="h-12 text-base border-2 border-[#D1C9C1]" />
              </div>
            </div>
            <div>
              <Label className="text-base font-semibold mb-1.5 block">Categoría</Label>
              <Select value={need.category} onValueChange={(v) => setNeed({ ...need, category: v })}>
                <SelectTrigger data-testid="edit-category" className="h-12 text-base border-2 border-[#D1C9C1]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="text-base py-3">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-base font-semibold mb-1.5 block">Título</Label>
              <Input data-testid="edit-title" value={need.title || ""} onChange={(e) => setNeed({ ...need, title: e.target.value })} className="h-12 text-base border-2 border-[#D1C9C1]" />
            </div>
            <div>
              <Label className="text-base font-semibold mb-1.5 block">Descripción</Label>
              <Textarea data-testid="edit-description" value={need.description || ""} onChange={(e) => setNeed({ ...need, description: e.target.value })} rows={4} className="text-base border-2 border-[#D1C9C1]" />
            </div>
            <div>
              <Label className="text-base font-semibold mb-1.5 block">Fecha en que se necesita la ayuda</Label>
              <Input data-testid="edit-needed-date" type="date" value={need.needed_date || ""} onChange={(e) => setNeed({ ...need, needed_date: e.target.value })} className="h-12 text-base border-2 border-[#D1C9C1]" />
            </div>
          </div>

          {need.volunteers && need.volunteers.length > 0 && (
            <div className="mt-6 bg-[#EAF2EE] border-2 border-[#2C5545]/30 rounded-lg p-4">
              <h3 className="font-serif text-lg font-semibold text-[#2C5545] mb-2">
                Personas que se han ofrecido a ayudar
              </h3>
              <ul className="space-y-2">
                {need.volunteers.map((v) => (
                  <li key={v.id} className="text-base text-[#1A1818]">
                    <strong>{v.name}</strong> — <span className="text-[#4A4646]">{v.contact}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="edit-save"
              onClick={save} disabled={saving}
              className="h-14 flex-1 text-base font-semibold bg-[#8A3A33] hover:bg-[#6B2B25] text-white"
            >
              <Save className="w-5 h-5 mr-2" />
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="edit-complete" className="h-14 text-base font-semibold bg-[#2C5545] hover:bg-[#1F3D32] text-white">
                  <CheckCircle2 className="w-5 h-5 mr-2" />
                  Marcar Completada
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-serif">¿Marcar como completada?</AlertDialogTitle>
                  <AlertDialogDescription className="text-base">
                    La necesidad será archivada y dejará de aparecer en el tablero principal.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="h-12 text-base">Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={markComplete} className="bg-[#2C5545] hover:bg-[#1F3D32] h-12 text-base" data-testid="confirm-complete">
                    Sí, marcar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="edit-delete" variant="outline" className="h-14 text-base font-semibold border-2 border-red-300 text-red-700 hover:bg-red-50">
                  <Trash2 className="w-5 h-5 mr-2" />
                  Eliminar
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-serif">¿Eliminar publicación?</AlertDialogTitle>
                  <AlertDialogDescription className="text-base">
                    Esta acción no se puede deshacer.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="h-12 text-base">Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={remove} className="bg-red-700 hover:bg-red-800 h-12 text-base" data-testid="confirm-delete">
                    Sí, eliminar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </div>
  );
}
